// hooks/useSSEAssignment.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { SSEService } from '../services/SSEService';

export const useSSEAssignment = (baseUrl, notificationService) => {
  const [isConnected, setIsConnected] = useState(false);
  const [progress, setProgress] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const sseServiceRef = useRef(null);
  
  // Initialize SSE service
  useEffect(() => {
    sseServiceRef.current = new SSEService(baseUrl);
    
    return () => {
      if (sseServiceRef.current) {
        sseServiceRef.current.cleanup();
      }
    };
  }, [baseUrl]);
  
  // Connect to SSE for assignment
  const startAssignment = useCallback(async () => {
    console.log('🔔 Starting SSE assignment connection...');
    if (!sseServiceRef.current) {
      console.error('🔔 No SSE service available');
      return null;
    }
    
    try {
      setIsAssigning(true);
      // Set initial progress so dialog shows immediately
      setProgress({
        step: 0,
        totalSteps: 4,
        message: 'Connecting to server...',
        details: {},
        percentage: 0
      });
      
      // Get session ID and connect
      console.log('🔔 Getting session ID...');
      const sessionId = await sseServiceRef.current.connect();
      console.log('🔔 Got session ID:', sessionId);
      
      // Set up event listeners
      const unsubscribers = [];
      
      unsubscribers.push(
        sseServiceRef.current.on('connected', () => {
          setIsConnected(true);
          console.log('SSE connected for assignment');
        })
      );
      
      unsubscribers.push(
        sseServiceRef.current.on('progress', (data) => {
          console.log('🔔 SSE Progress:', data);
          setProgress({
            step: data.step,
            totalSteps: data.totalSteps,
            message: data.message,
            details: data.details,
            percentage: Math.round((data.step / data.totalSteps) * 100)
          });
          
          // Don't show individual notifications - let the progress dialog handle it
        })
      );
      
      unsubscribers.push(
        sseServiceRef.current.on('complete', (data) => {
          console.log('🔔 SSE Complete:', data);
          setIsConnected(false);
          setIsAssigning(false);
          setProgress(null);
          
          // Show single success notification at the end
          if (notificationService && data.result) {
            const { details } = data.result;
            const message = `Assignment complete! Updated ${details.defaultRoutesUpdated.count} default routes and ${details.nonDefaultRoutesUpdated.count} future routes.`;
            notificationService.success(message);
          }
        })
      );
      
      unsubscribers.push(
        sseServiceRef.current.on('error', (data) => {
          console.log('🔔 SSE Error:', data);
          setIsConnected(false);
          setIsAssigning(false);
          
          // Show error in progress dialog briefly before closing
          setProgress({
            step: -1,
            totalSteps: 4,
            message: data.error || 'Assignment failed',
            details: {},
            percentage: 0
          });
          
          // Close dialog and show notification after a delay
          setTimeout(() => {
            setProgress(null);
            if (notificationService) {
              notificationService.error(data.error || 'Assignment failed');
            }
          }, 2000);
        })
      );
      
      // Store unsubscribers for cleanup
      sseServiceRef.current._unsubscribers = unsubscribers;
      
      return sessionId;
    } catch (error) {
      console.error('🔔 Failed to start SSE assignment:', error);
      console.error('🔔 Error details:', error.response?.data || error.message);
      setIsAssigning(false);
      if (notificationService) {
        notificationService.error('Failed to connect for real-time updates');
      }
      return null;
    }
  }, [notificationService]);
  
  // End assignment (cleanup)
  const endAssignment = useCallback(() => {
    if (sseServiceRef.current) {
      // Clean up listeners
      if (sseServiceRef.current._unsubscribers) {
        sseServiceRef.current._unsubscribers.forEach(unsub => unsub());
        sseServiceRef.current._unsubscribers = [];
      }
      
      // Disconnect
      sseServiceRef.current.disconnect();
      
      setIsConnected(false);
      setIsAssigning(false);
      setProgress(null);
    }
  }, []);
  
  return {
    isConnected,
    isAssigning,
    progress,
    startAssignment,
    endAssignment
  };
};